// ink-component
import React, { useEffect, useState, ReactNode, useContext } from 'react';

import { bemHelper, injectChild } from '../../../helpers';

import useDeprecatedWarning from '../../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../../helpers/constants/global-props';

import SelectableCell from '../SelectableCell/SelectableCell';
import TableCell from '../TableCell/TableCell';

import Icon from '../../Icon/Icon';
import TableContext from '../TableContext/TableContext';

type Ribbon = {
  text: string;
  color: 'green' | 'red' | 'blue' | 'orange' | 'yellow' | 'gray' | 'purple' | 'pink';
};

export type ChildProps = {
  shaded?: boolean;
  highlight?: 'blue' | 'green' | 'red' | 'yellow' | 'white';
  twiddle?: boolean;
  onClick?: () => void;
  twiddleContent?: boolean;
};

export interface TableBodyRowProps {
  /** Contents */
  children?: any;
  /** Type of the row ribbon */
  ribbon?: string | Ribbon;
  /** Row indentation */
  indent?: 1 | 2 | 3 | 4;
  /** Adds a color stripe to the row. All the rows in a table need to have stripe for it to function properly. */
  stripe?: 'blue' | 'brown' | 'grey' | 'green' | 'pink';
  /** onClick callback for the row. It will not be called if the content of the
   * row is editable - such as Inputs or Select components */
  onClick?: () => void;
  /** Highlights an entire row with the chosen color */
  highlight?: 'blue' | 'green' | 'red' | 'yellow' | 'white';
  /** Modifier for shaded rows */
  shaded?: boolean;
  /** Highlights the Row and adds the `bold` property to it.
   * When a row is `total` it can also receive the `transparent` property. */
  total?: boolean;
  /** Makes `total` rows transparent */
  transparent?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  twiddleContent?: boolean;
  /** @ignore */
  twiddle?: boolean;
  /** @ignore */
  trigger?: any;
  /** @ignore */
  isOpen?: boolean;
  /** @ignore */
  rowIndex?: number;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @categories table
 * @purpose Wraps cells to create a row inside the table body
 * @deprecated This component is deprecated and replaced by [NewTable.Row](https://ink.carta.com/components/NewTableRow/)
 * @status deprecated
 * @replacementComponent NewTable.Row
 * @dateOfDeletion December 1st, 2023
 */
function TableBodyRow(props: TableBodyRowProps) {
  useDeprecatedWarning({ componentName: 'TableBodyRow', substitute: 'NewTable.Row' });

  const { hover, selectable, selected, toggle, toggleable } = useContext(TableContext);
  const [open, setOpen] = useState<boolean | undefined>(props.isOpen);

  useEffect(() => {
    setOpen(props.isOpen);
  }, [props.isOpen]);

  const toggleTwiddle = (): void => {
    setOpen(prevOpen => {
      // @ts-ignore
      toggle(!prevOpen, props.rowIndex);
      return !prevOpen;
    });
  };

  const triggerOnClick = (): void => {
    if (props.onClick) {
      props.onClick();
    }
    if (props.twiddle) {
      toggleTwiddle();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.keyCode === 13) triggerOnClick();
  };

  const getChildProps = (): ChildProps => {
    const { shaded, twiddle, twiddleContent, highlight } = props;
    const childProps: ChildProps = {
      twiddle,
      onClick: triggerOnClick,
      twiddleContent,
    };

    if (highlight) {
      return { ...childProps, highlight };
    }
    if (shaded) return { ...childProps, shaded };

    return childProps;
  };

  const defineClassName = (): string => {
    const { rowIndex, twiddle, transparent, total, onClick } = props;
    const b = bemHelper();

    if (total) {
      return b('ink-table--total-row', {
        transparent,
      });
    }

    return b('ink-table--body--row', {
      hover: !!onClick || hover,
      twiddle,
      twiddle__open: twiddle && open,
      // @ts-ignore
      selected: Boolean(selectable && selected.includes(rowIndex)),
    });
  };

  /* Rows can receive Cells as their children */
  const renderCells = (): ReactNode => {
    const { children, ribbon, indent, stripe, twiddleContent, highlight, isOpen } = props;

    const childProps = getChildProps();

    const firstChildProps = {
      ...childProps,
      indent,
      ribbon: !(toggleable || selectable) || (toggleable && twiddleContent && isOpen) ? ribbon : null,
      stripe: !(toggleable || selectable) || (toggleable && twiddleContent && isOpen) ? stripe : null,
    };

    return React.Children.map(children, (child, index) => {
      if (index === 0) {
        return (
          <>
            {Boolean(twiddleContent && stripe) && (
              <TableCell shaded={props.shaded} stripe="shaded" twiddleContent freeze={child.props.freeze} />
            )}
            <SelectableCell {...props} index={index} freeze={child.props.freeze} />
            {toggleable && (
              <>
                {twiddleContent ? (
                  <TableCell shaded ribbon={props.ribbon} stripe={props.stripe} freeze={child.props.freeze} />
                ) : (
                  <TableCell shaded={props.shaded} checkbox highlight={highlight} freeze={child.props.freeze} />
                )}
              </>
            )}
            {injectChild(child, firstChildProps)}
          </>
        );
      }
      if (index === React.Children.count(children) - 1) {
        return injectChild(child, { ...childProps, last: true });
      }
      return injectChild(child, childProps);
    });
  };

  const renderTrigger = (): ReactNode => {
    const { highlight, trigger, shaded } = props;
    return React.Children.map(trigger.props.children, child =>
      injectChild(child, { onClick: triggerOnClick, highlight, shaded }),
    );
  };

  const renderRows = (): ReactNode => {
    const { children } = props;
    return React.Children.map(children, child => {
      const twiddleIndent = child.props.indent || 1;
      return injectChild(child, {
        twiddleContent: true,
        indent: twiddleIndent,
      });
    });
  };

  if (props.trigger) {
    return (
      <>
        <tr tabIndex={0} onKeyDown={onKeyDown} className={defineClassName()} data-testid={props['data-testid']}>
          <SelectableCell
            {...props}
            twiddle={false}
            index={0}
            freeze={props?.trigger?.props?.children[0]?.props?.freeze}
          />
          {toggleable && (
            <TableCell
              shaded={props.shaded}
              ribbon={!selectable ? props.ribbon : undefined}
              stripe={!selectable ? props.stripe : undefined}
              onClick={triggerOnClick}
              highlight={props.highlight}
              freeze={props?.trigger?.props?.children[0]?.props?.freeze}
              checkbox
            >
              <div className="ink-table--cell--twiddle">
                <Icon type="forward" />
              </div>
            </TableCell>
          )}
          {renderTrigger()}
        </tr>
        {open && renderRows()}
      </>
    );
  }

  return (
    <tr className={defineClassName()} data-testid={props['data-testid']} {...globalProps}>
      {renderCells()}
    </tr>
  );
}

TableBodyRow.defaultProps = {
  children: undefined,
  ribbon: undefined,
  indent: undefined,
  stripe: undefined,
  onClick: undefined,
  highlight: undefined,
  shaded: undefined,
  total: false,
  twiddle: false,
  trigger: undefined,
  transparent: false,
};

TableBodyRow.displayName = 'TableBodyRow';

export default TableBodyRow;
